import { t as tCem } from '../utils/i18n'
/**
 * MapView — Mapa de red en tiempo real
 *
 * Modo completo (sin selección): mapa a pantalla completa con markers y filtros.
 * Modo split (sitio seleccionado): grid 2×2
 *   Q2 sup-izq : mapa reducido con badge del sitio seleccionado
 *   Q1 sup-der : mini reproductor en vivo (MJPEG server-side, 1 canal a la vez)
 *   Q3 inf-izq : incidentes activos del sitio
 *   Q4 inf-der : estado de salud del sistema (edge + dispositivos)
 *
 * Nota técnica: mapRef nunca sale del DOM (MapLibre no soporta re-mount).
 * Al cambiar el layout se llama map.resize() tras el repaint del navegador.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { Phone } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import WhepPlayer from '../components/WhepPlayer'
import HlsPlayer, { buildHlsUrl } from '../components/HlsPlayer'
import ErrorBoundary from '../components/ErrorBoundary'

// Sprint planograma 2026-06-12 — los 4 cuadrantes del modo split se rediseñan
import MapViewQ1Unified    from '../components/MapViewQ1Unified'
import MapViewQ2Incidents  from '../components/MapViewQ2Incidents'
import MapViewQ3Timeline   from '../components/MapViewQ3Timeline'
import MapViewQ4Planogram  from '../components/MapViewQ4Planogram'

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  ok:      '#10b981',
  warning: '#f59e0b',
  alarm:   '#ef4444',
  offline: '#6b7280',
}
const STATUS_LABEL = {
  ok:      'Normal',
  warning: tCem('Advertencia','Warning'),
  alarm:   tCem('Alarma','Alarm'),
  offline: 'Sin conexión',
}
const SEV_COLOR = {
  critical: '#7c3aed',
  high:     '#ef4444',
  medium:   '#f59e0b',
  low:      '#10b981',
}

const DEFAULT_CENTER = [-70.6693, -33.4489]
const DEFAULT_ZOOM   = 6

// ─── Helpers ──────────────────────────────────────────────────────────────────

function injectStyles() {
  if (document.getElementById('mapview-styles')) return
  const s = document.createElement('style')
  s.id = 'mapview-styles'
  s.textContent = `
    @keyframes pulse-ring {
      0%   { transform: scale(0.8); opacity: 0.8; }
      100% { transform: scale(1.6); opacity: 0; }
    }
    @keyframes mv-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(s)
}

function createMarkerEl(status, pulse = false) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.ok
  const el = document.createElement('div')
  el.style.cssText = `
    width:28px;height:28px;cursor:pointer;
    display:flex;align-items:center;justify-content:center;
    position:relative;
  `
  if (pulse && status === 'alarm') {
    const ring = document.createElement('div')
    ring.style.cssText = `
      position:absolute;inset:-4px;border-radius:50%;
      border:2px solid ${color};
      animation:pulse-ring 1.5s ease-out infinite;opacity:0.6;
    `
    el.appendChild(ring)
  }
  el.innerHTML += `
    <svg width="28" height="32" viewBox="0 0 28 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 18 14 18s14-8.667 14-18C28 6.268 21.732 0 14 0z"
            fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="14" cy="13" r="5" fill="white" opacity="0.9"/>
    </svg>
  `
  return el
}

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (diff < 1)    return 'Ahora'
  if (diff < 60)   return `${diff}m`
  if (diff < 1440) return `${Math.floor(diff / 60)}h`
  return `${Math.floor(diff / 1440)}d`
}

// ─── Shared mini-components ───────────────────────────────────────────────────

function PanelHeader({ icon, title, subtitle, badge, extra }) {
  return (
    <div style={{
      padding: '10px 12px',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
    }}>
      <span style={{ fontSize: 14, opacity: 0.7 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
          {badge && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
              background: badge.color + '20', color: badge.color,
            }}>{badge.count}</span>
          )}
        </div>
        {subtitle && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{subtitle}</div>
        )}
      </div>
      {extra}
    </div>
  )
}

function Spinner() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: 22, height: 22, borderRadius: '50%',
        border: '2px solid var(--border)',
        borderTopColor: 'var(--accent)',
        animation: 'mv-spin 0.8s linear infinite',
      }} />
    </div>
  )
}

function EmptyState({ icon = '○', text, color = 'var(--text-muted)' }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 8,
      color: 'var(--text-muted)',
    }}>
      <span style={{ fontSize: 28, color }}>{icon}</span>
      <span style={{ fontSize: 12 }}>{text}</span>
    </div>
  )
}

const PANEL_STYLE = {
  background: 'var(--bg-surface)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
}

// ─── Q1: Mini reproductor en vivo ─────────────────────────────────────────────
//
// Stream server-side MJPEG (mismo endpoint que MonitorView).
// Una sola cámara activa a la vez (cada <img> abierto = 1 pipe RTSP→JPEG en
// el backend, así que NO autoplay de varias). Selector de NVR + canal arriba,
// botón "Monitor ↗" para saltar a la vista de operación completa.

// WhepPlayer importado de components/WhepPlayer — usado también en MonitorView.
// Lo de abajo es la implementación vieja, dejada como reference temporal.
// eslint-disable-next-line no-unused-vars
function _WhepPlayer_OLD_UNUSED({ siteId, channel, channelName, token, onFallback }) {
  const videoRef = useRef(null)
  const pcRef    = useRef(null)
  const sessRef  = useRef(null)  // { whep_url, stream_token, location_url }
  const [state, setState] = useState('connecting')  // connecting|playing|error
  const [err,   setErr]   = useState(null)

  useEffect(() => {
    let cancelled = false
    let pc = null

    async function start() {
      try {
        console.log('[WHEP] start', { siteId, channel })
        setState('connecting')
        setErr(null)

        // 1) Grant del backend
        console.log('[WHEP] requesting grant…')
        const gr = await fetch(`/api/streams/${siteId}/grant?channel=${channel}`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
        if (!gr.ok) {
          const t = await gr.text()
          throw new Error(`grant ${gr.status}: ${t}`)
        }
        const grant = await gr.json()
        console.log('[WHEP] grant OK', { whep_url: grant.whep_url })
        if (cancelled) return

        // 2) Crear RTCPeerConnection con STUN+TURN del grant.
        // STUN ayuda a descubrir IP pública; TURN actúa como relay si direct
        // UDP no funciona (típico en macOS con utun de Tailscale que no
        // enruta UDP P2P).
        const iceServers = grant.ice_servers || []
        // Policy: 'all' por default. ?webrtc_policy=relay para forzar
        // solo TURN (debug útil cuando ICE direct falla).
        const policy = /webrtc_policy=relay/.test(window.location.search)
          ? 'relay' : 'all'
        console.log('[WHEP] using', iceServers.length, 'ICE servers, policy:', policy)
        pc = new RTCPeerConnection({
          iceServers,
          bundlePolicy: 'max-bundle',
          iceTransportPolicy: policy,
        })
        pcRef.current = pc
        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.ontrack = (e) => {
          console.log('[WHEP] ontrack', e.track.kind, e.streams.length, 'streams')
          if (videoRef.current && e.streams[0]) {
            videoRef.current.srcObject = e.streams[0]
          }
        }
        pc.oniceconnectionstatechange = () => {
          const s = pc.iceConnectionState
          console.log('[WHEP] ice state →', s)
          if (s === 'connected' || s === 'completed') setState('playing')
          else if (s === 'failed' || s === 'disconnected') {
            setState('error'); setErr('ICE failed')
          }
        }

        // 3) SDP offer + POST WHEP
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)
        console.log('[WHEP] local desc set, posting offer to', grant.whep_url)

        const ar = await fetch(grant.whep_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            'Authorization': `Bearer ${grant.stream_token}`,
          },
          body: offer.sdp,
        })
        console.log('[WHEP] WHEP response', ar.status, 'content-type:', ar.headers.get('content-type'))
        if (!ar.ok) {
          const body = await ar.text()
          throw new Error(`WHEP ${ar.status}: ${body.substring(0, 200)}`)
        }
        const answerSdp = await ar.text()
        const locationUrl = ar.headers.get('Location')
        console.log('[WHEP] answer SDP len', answerSdp.length, 'location:', locationUrl)
        sessRef.current = {
          whep_url: grant.whep_url,
          stream_token: grant.stream_token,
          location_url: locationUrl,
        }

        if (cancelled) return
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
        console.log('[WHEP] remote desc set, waiting for ICE')
      } catch (e) {
        if (cancelled) return
        console.error('[WHEP] error', e)
        setErr(e.message || String(e))
        setState('error')
      }
    }
    start()

    return () => {
      cancelled = true
      // DELETE session — Location ya viene como ruta absoluta del backend
      if (sessRef.current?.location_url && sessRef.current?.stream_token) {
        fetch(sessRef.current.location_url, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${sessRef.current.stream_token}` },
          keepalive: true,
        }).catch(() => {})
      }
      if (pc) {
        try { pc.close() } catch {}
      }
    }
  }, [siteId, channel, token])

  return (
    <div style={{
      flex: 1, position: 'relative', background: '#000',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center', minHeight: 0,
    }}>
      <video
        ref={videoRef}
        autoPlay playsInline muted
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain', display: 'block',
        }}
      />
      {state === 'connecting' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, color: '#e5e5e5',
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff',
            animation: 'mv-spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 11 }}>Conectando WebRTC…</span>
        </div>
      )}
      {state === 'error' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column', gap: 8,
          alignItems: 'center', justifyContent: 'center', color: '#e5e5e5',
        }}>
          <span style={{ fontSize: 12 }}>WebRTC falló</span>
          <span style={{ fontSize: 10, color: '#aaa', maxWidth: '80%', textAlign: 'center' }}>
            {err}
          </span>
          <button
            onClick={onFallback}
            style={{
              padding: '5px 12px', fontSize: 10, fontWeight: 600,
              background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer',
            }}
          >Usar MJPEG vía cloud</button>
        </div>
      )}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
        padding: '14px 10px 6px 10px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, pointerEvents: 'none',
      }}>
        <span style={{
          fontSize: 11, color: '#e5e5e5', fontWeight: 500,
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        }}>{channelName}</span>
        <span style={{
          fontSize: 9, fontWeight: 700,
          color: state === 'playing' ? '#10b981' : '#f59e0b',
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          letterSpacing: '0.05em',
        }}>● RTC {state === 'playing' ? 'DIRECT' : state.toUpperCase()}</span>
      </div>
    </div>
  )
}


function MiniLivePlayer({ nvr, channel, channelName, token, streamQuality }) {
  const imgRef    = useRef(null)
  const retryRef  = useRef(null)        // timeout activo
  const attemptRef = useRef(0)          // intentos consecutivos sin frame
  const [retrying, setRetrying] = useState(false)

  const streamUrl = useCallback((bust = false) => {
    if (!nvr || !channel) return ''
    const auth = `?token=${encodeURIComponent(token)}`
    const cb   = bust ? `&_=${Date.now()}` : ''
    // Caso "1 cámara visible" (MapView Q1, MonitorView single-cell). NUNCA
    // hay >1 stream HD simultáneo del mismo operador. Por ende cuando el
    // operador elige HD le damos full resolution sin ahorrar nada:
    //
    //   Main → 1920 px @ 25 fps  (cap del backend; el NVR entrega 25)
    //   Sub  →  640 px @ 15 fps  (modo "no me importa la calidad")
    //
    // El multi-grid del Monitor usa SU propio componente CameraCell y va
    // siempre a sub stream (esta vista no entra en ese caso).
    const main = streamQuality === 'main'
    const w    = main ? 1920 : 640
    const fps  = main ? 25   : 15
    return `/api/nvr/${nvr.id}/live/${channel}${auth}&fps=${fps}&width=${w}&stream=${streamQuality}${cb}`
  }, [nvr, channel, token, streamQuality])

  const clearRetry = useCallback(() => {
    if (retryRef.current) {
      clearTimeout(retryRef.current)
      retryRef.current = null
    }
  }, [])

  const openStream = useCallback((bust = false) => {
    if (!imgRef.current) return
    setRetrying(false)
    imgRef.current.src = streamUrl(bust)
  }, [streamUrl])

  const scheduleRetry = useCallback(() => {
    clearRetry()
    attemptRef.current += 1
    // backoff exponencial acotado: 1s, 2s, 4s, 8s, máx 8s.
    // El RTSP del NVR puede tardar 2-3s en responder tras un corte,
    // así que arrancamos en 1s para no martillar el backend.
    const delay = Math.min(8000, 1000 * 2 ** (attemptRef.current - 1))
    setRetrying(true)
    retryRef.current = setTimeout(() => openStream(true), delay)
  }, [clearRetry, openStream])

  // Re-abrir el stream cuando cambia nvr o canal
  // Deps SOLO primitivas, no callbacks. Antes se reseteaba el stream cada
  // vez que el parent re-renderizaba porque streamUrl/openStream/clearRetry
  // eran refs nuevas. Ahora estable.
  useEffect(() => {
    clearRetry()
    attemptRef.current = 0
    setRetrying(false)
    if (imgRef.current) {
      imgRef.current.src = ''
      const t = setTimeout(() => openStream(false), 80)
      return () => { clearTimeout(t); clearRetry() }
    }
    return () => clearRetry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nvr?.id, channel, streamQuality, token])

  // Cuando llega frame OK, resetear contador de intentos
  function onLoadFrame() {
    if (attemptRef.current !== 0) attemptRef.current = 0
    if (retrying) setRetrying(false)
  }

  return (
    <div style={{
      flex: 1, position: 'relative',
      background: '#000', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 0,
    }}>
      <img
        ref={imgRef}
        alt={channelName}
        onLoad={onLoadFrame}
        onError={scheduleRetry}
        style={{
          maxWidth: '100%', maxHeight: '100%',
          width: '100%', height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />

      {/* Overlay "Reconectando…" sobre el último frame conocido (o sobre negro) */}
      {retrying && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8,
          color: '#e5e5e5', pointerEvents: 'none',
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.25)',
            borderTopColor: '#fff',
            animation: 'mv-spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 11, letterSpacing: '0.04em' }}>
            Reconectando…
          </span>
        </div>
      )}

      {/* Barra inferior — nombre + LIVE/RECONECTANDO */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
        padding: '14px 10px 6px 10px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, pointerEvents: 'none',
      }}>
        <span style={{
          fontSize: 11, color: '#e5e5e5', fontWeight: 500,
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{channelName}</span>
        <span style={{
          fontSize: 9, fontWeight: 700,
          color: retrying ? '#f59e0b' : '#10b981',
          letterSpacing: '0.05em',
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        }}>{retrying ? '● RECONECTANDO' : '● LIVE'}</span>
      </div>
    </div>
  )
}

// Mini barra de conteo bajo el live player — pide /api/analytics/counts por
// la cámara activa y refresca cada 20s. Compacta a 2 líneas:
//   🚗 12  car·11 trk·1   →6 ←5 ⊝1
//   🚶  5  este·2 oeste·3
function CountsStrip({ siteId, channel, token }) {
  const [data,    setData]    = useState(null)
  const [window,  setWindow]  = useState(60)   // minutos
  const [err,     setErr]     = useState(false)
  const [lastTs,  setLastTs]  = useState(null) // momento del último fetch OK
  const [tick,    setTick]    = useState(0)    // re-render para refrescar "hace Ns"

  const load = useCallback(async () => {
    if (!siteId || !channel) return
    try {
      // Usamos site_id + channel; el backend resuelve el camera_name real
      // contra remote_config del nodo edge. Esto evita el mismatch entre los
      // nombres del firmware NVR ("Camera 01") y los nombres del operador
      // ("Cámara 5") que el edge pone en cada Event.
      const url = `/api/analytics/counts?site_id=${siteId}&channel=${channel}&window_minutes=${window}`
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) { setErr(true); return }
      const j = await r.json()
      setData(j); setErr(false); setLastTs(Date.now())
    } catch { setErr(true) }
  }, [siteId, channel, window, token])

  // Carga inicial al cambiar cámara/ventana
  useEffect(() => { load() }, [load])
  // Polling agresivo cada 3s — el endpoint es liviano (3 queries agrupadas
  // contra un índice en event_ts + camera_name). 3s es buen punto entre
  // "se siente tiempo real" y "no martilla la DB".
  useEffect(() => {
    const id = setInterval(load, 3000)
    return () => clearInterval(id)
  }, [load])
  // Re-render cada 1s para que el contador "hace Ns" avance
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  if (err || !data) {
    return (
      <div style={{
        borderTop: '1px solid var(--border)', padding: '6px 10px',
        fontSize: 10, color: 'var(--text-muted)', textAlign: 'center',
        flexShrink: 0, background: 'var(--bg-surface)',
      }}>
        {err ? tCem('Sin conteos disponibles','No counts available') : tCem('Cargando…','Loading…')}
      </div>
    )
  }

  // Guards defensivos — el endpoint /api/analytics/counts puede devolver
  // shape distinto según vertical (retail vs EDS) o si la cámara no tiene
  // tracker activo. Sin esto, undefined.by_type lanza TypeError que el
  // ErrorBoundary captura como "vehicle_summary" o similares en el bundle
  // minificado.
  const v = data.vehicles || { total: 0, by_type: {}, by_dir: { right:0, left:0, idle:0 } }
  const p = data.persons  || { total: 0, by_dir: { east:0, west:0, idle:0 } }
  const types = Object.entries(v.by_type || {})
  const vdir  = v.by_dir  || { right:0, left:0, idle:0 }
  const pdir  = p.by_dir  || { east:0, west:0, idle:0 }

  // Helpers para colorear cero (gris) vs > 0 (texto principal)
  const num = (n) => ({
    fontWeight: 700,
    color: n > 0 ? 'var(--text-primary)' : 'var(--text-muted)',
  })

  return (
    <div style={{
      borderTop: '1px solid var(--border)', flexShrink: 0,
      background: 'var(--bg-surface)', fontSize: 11,
    }}>
      {/* Header con selector de ventana + indicador de refresh */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '5px 10px', borderBottom: '1px solid var(--border)', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: err ? '#ef4444' : '#10b981', flexShrink: 0,
            animation: !err && lastTs && (Date.now() - lastTs < 1500)
                       ? 'mv-spin 0.6s ease-out' : 'none',
            opacity: !err && lastTs && (Date.now() - lastTs < 1500) ? 1 : 0.6,
            transition: 'opacity 0.4s',
          }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Conteo
          </span>
          {lastTs && (
            <span style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 400 }}>
              · {Math.max(0, Math.floor((Date.now() - lastTs)/1000))}s
            </span>
          )}
        </div>
        <select
          value={window}
          onChange={e => setWindow(Number(e.target.value))}
          style={{
            fontSize: 10, padding: '1px 4px', borderRadius: 3,
            background: 'var(--bg-root)', color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
          }}
        >
          <option value={15}>15 min</option>
          <option value={60}>1 h</option>
          <option value={240}>4 h</option>
          <option value={1440}>24 h</option>
        </select>
      </div>

      {/* Línea vehículos */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 10px', borderBottom: '1px solid var(--border)',
      }}>
        <span style={{ fontSize: 14 }}>🚗</span>
        <span style={num(v.total)}>{v.total}</span>
        {types.length > 0 && (
          <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
            {types.map(([t, n]) => `${t}·${n}`).join(' ')}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span title="Hacia la derecha" style={num(vdir.right)}>→ {vdir.right}</span>
        <span title="Hacia la izquierda" style={num(vdir.left)}>← {vdir.left}</span>
        <span title="Sin desplazamiento" style={{ ...num(vdir.idle), opacity: 0.7 }}>⊝ {vdir.idle}</span>
      </div>

      {/* Línea personas */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 10px',
      }}>
        <span style={{ fontSize: 14 }}>🚶</span>
        <span style={num(p.total)}>{p.total}</span>
        <span style={{ flex: 1 }} />
        <span title="Hacia el este (de frente)" style={num(pdir.east)}>↦ E {pdir.east}</span>
        <span title="Hacia el oeste (espaldas)" style={num(pdir.west)}>↤ O {pdir.west}</span>
        <span title="Sin desplazamiento" style={{ ...num(pdir.idle), opacity: 0.7 }}>⊝ {pdir.idle}</span>
      </div>

      {/* Indicador "en frame ahora" si hay actividad */}
      {(data.active_now?.vehicles > 0 || data.active_now?.persons > 0) && (
        <div style={{
          padding: '3px 10px',
          fontSize: 9, color: '#10b981', fontWeight: 600,
          background: 'rgba(16,185,129,0.08)',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>● En frame ahora</span>
          <span>
            {data.active_now.vehicles > 0 && `🚗 ${data.active_now.vehicles} `}
            {data.active_now.persons > 0 && `🚶 ${data.active_now.persons}`}
          </span>
        </div>
      )}
    </div>
  )
}

function LivePreviewPanel({ site, token }) {
  const [nvrs,      setNvrs]      = useState([])
  const [activeNvr, setActiveNvr] = useState(null)
  const [channels,  setChannels]  = useState([])
  const [chNum,     setChNum]     = useState(null)
  const [loading,   setLoading]   = useState(true)
  // Calidad preferida del operador. Persiste en localStorage para que cada
  // operador conserve su elección (main = HD subtype 0, sub = SD subtype 1).
  const [quality,   setQuality]   = useState(() => {
    try { return localStorage.getItem('retaliq_map_quality') || 'main' }
    catch { return 'main' }
  })
  useEffect(() => {
    try { localStorage.setItem('retaliq_map_quality', quality) } catch {}
  }, [quality])

  // Cargar NVRs del sitio
  useEffect(() => {
    if (!site) return
    let cancelled = false
    setLoading(true)
    setNvrs([])
    setActiveNvr(null)
    setChannels([])
    setChNum(null)
    fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(all => {
        if (cancelled) return
        const forSite = all.filter(n => n.site_id === site.id)
        setNvrs(forSite)
        setActiveNvr(forSite[0] || null)
        if (forSite.length === 0) setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [site?.id, token])

  // Cargar canales del NVR activo
  useEffect(() => {
    if (!activeNvr) return
    let cancelled = false
    setLoading(true)
    fetch(`/api/nvr/${activeNvr.id}/channels`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(chs => {
        if (cancelled) return
        setChannels(chs)
        setChNum(chs[0]?.channel ?? 1)
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [activeNvr?.id, token])

  const activeChannel = channels.find(c => c.channel === chNum)
  const channelLabel  = activeChannel
    ? `CH${String(activeChannel.channel).padStart(2,'0')} · ${activeChannel.name}`
    : (chNum ? `CH${String(chNum).padStart(2,'0')}` : '')

  function openMonitor() {
    // Abre MonitorView en otra pestaña — UX consistente con incidentes.
    window.open(`/monitor?site=${site.id}`, `mon_${site.id}`, 'noopener')
  }

  return (
    <div style={PANEL_STYLE}>
      <PanelHeader
        icon="◉"
        title="Vivo"
        subtitle={activeNvr ? activeNvr.name : (nvrs.length === 0 ? tCem('Sin NVR','No NVR') : tCem('Cargando…','Loading…'))}
        badge={channels.length > 0
          ? { count: `${channels.length} canales`, color: '#10b981' }
          : null}
        extra={
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {/* Toggle Main / Sub */}
            <div style={{
              display: 'flex', borderRadius: 4,
              border: '1px solid var(--border)', overflow: 'hidden',
            }}>
              {['rtc', 'main', 'sub'].map(q => (
                <button
                  key={q}
                  onClick={() => setQuality(q)}
                  title={q === 'rtc'
                    ? 'WebRTC directo edge↔operador (sub-segundo, requiere Tailscale)'
                    : q === 'main'
                    ? 'MJPEG vía cloud — HD (300ms latencia)'
                    : 'MJPEG vía cloud — SD (ahorra banda)'}
                  style={{
                    padding: '3px 7px', fontSize: 9, fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    background: quality === q ? 'var(--accent)' : 'var(--bg-surface)',
                    color: quality === q ? '#fff' : 'var(--text-muted)',
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}
                >{q === 'rtc' ? 'RTC' : q === 'main' ? 'HD' : 'SD'}</button>
              ))}
            </div>
            <button
              onClick={openMonitor}
              title="Abrir vista Monitor"
              style={{
                padding: '4px 9px', fontSize: 10, fontWeight: 600,
                borderRadius: 4, border: '1px solid var(--border)',
                background: 'var(--bg-surface)', color: 'var(--text-secondary)',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >Monitor ↗</button>
          </div>
        }
      />

      {/* Selector de NVR (solo si hay más de uno) + canal */}
      {(nvrs.length > 1 || channels.length > 0) && (
        <div style={{
          padding: '6px 10px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0,
        }}>
          {nvrs.length > 1 && (
            <select
              value={activeNvr?.id || ''}
              onChange={e => {
                const id = Number(e.target.value)
                const n = nvrs.find(x => x.id === id)
                if (n) setActiveNvr(n)
              }}
              style={{
                fontSize: 11, padding: '3px 6px', borderRadius: 4,
                background: 'var(--bg-root)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', flex: 1, minWidth: 0,
              }}
            >
              {nvrs.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          )}
          {channels.length > 0 && (
            <select
              value={chNum || ''}
              onChange={e => setChNum(Number(e.target.value))}
              style={{
                fontSize: 11, padding: '3px 6px', borderRadius: 4,
                background: 'var(--bg-root)', color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                flex: nvrs.length > 1 ? 1 : '1 1 100%', minWidth: 0,
              }}
            >
              {channels.map(c => (
                <option key={c.channel} value={c.channel}>
                  CH{String(c.channel).padStart(2,'0')} · {c.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {loading
        ? <Spinner />
        : (!activeNvr || channels.length === 0)
          ? <EmptyState icon="◉" text={nvrs.length === 0 ? 'Sin NVR configurado' : 'Sin canales'} />
          : (
            <>
              {(() => {
                // Si el sitio tiene m4_host configurado (Caddy local +
                // MediaMTX HLS expuesto via Tailscale) y la calidad pedida
                // es main (HD), preferimos HLS direct sin pasar por la
                // nube. Para sub (SD) o sitios sin m4_host, sigue MJPEG.
                const hlsUrl = quality === 'main'
                  ? buildHlsUrl(site?.id, activeNvr?.id, chNum)
                  : null
                if (quality === 'rtc') {
                  return (
                    <WhepPlayer
                      siteId={site?.id}
                      channel={chNum}
                      channelName={channelLabel}
                      token={token}
                      onFallback={() => setQuality('main')}
                    />
                  )
                }
                if (hlsUrl) {
                  return (
                    <HlsPlayer
                      url={hlsUrl}
                      channelName={channelLabel}
                      onFallback={() => setQuality('sub')}
                    />
                  )
                }
                return (
                  <MiniLivePlayer
                    nvr={activeNvr}
                    channel={chNum}
                    channelName={channelLabel}
                    token={token}
                    streamQuality={quality}
                  />
                )
              })()}
              <CountsStrip
                siteId={site?.id}
                channel={chNum}
                token={token}
              />
            </>
          )
      }
    </div>
  )
}

// ─── Q3: Panel de incidentes ──────────────────────────────────────────────────

function IncidentCard({ inc }) {
  const color = SEV_COLOR[inc.severity] || '#6b7280'
  const ago   = timeAgo(inc.created_at)

  return (
    <div
      onClick={() => window.dispatchEvent(new CustomEvent('open-incident', { detail: { id: inc.id } }))}
      style={{
        padding: '8px 10px', borderRadius: 6,
        border: '1px solid var(--border)',
        background: 'var(--bg-root)',
        marginBottom: 5, cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-root)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 500, marginBottom: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{inc.title}</div>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
              background: color + '22', color,
            }}>{inc.severity?.toUpperCase()}</span>
            <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{inc.status}</span>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }}>{ago}</span>
      </div>
    </div>
  )
}

function IncidentsPanel({ site, token }) {
  const [incidents, setIncidents] = useState([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    if (!site) return
    setLoading(true)
    fetch(`/api/incidents/?site_id=${site.id}&status=open&limit=20`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => { setIncidents(d.items || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [site?.id, token])

  return (
    <div style={PANEL_STYLE}>
      <PanelHeader
        icon="⚡"
        title="Incidentes activos"
        badge={incidents.length > 0
          ? { count: incidents.length, color: '#ef4444' }
          : null}
      />

      {loading
        ? <Spinner />
        : incidents.length === 0
          ? <EmptyState icon="✓" text="Sin incidentes activos" color="var(--success)" />
          : (
            <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
              {incidents.map(inc => <IncidentCard key={inc.id} inc={inc} />)}
            </div>
          )
      }

      <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <button
          style={{
            width: '100%', padding: '7px', borderRadius: 6, border: 'none',
            background: 'var(--accent)', color: '#fff',
            fontSize: 11, fontWeight: 500, cursor: 'pointer',
          }}
          onClick={() => alert('Crear incidente — próximamente')}
        >
          + Nuevo incidente
        </button>
      </div>
    </div>
  )
}

// ─── Q4: Panel de salud del sistema ───────────────────────────────────────────

function HealthRow({ icon, label, value, color, sub }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 2px', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 18, textAlign: 'center' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</div>
        {sub && <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>{value}</span>
    </div>
  )
}

// Barra horizontal para CPU/MEM/Disk del edge node.
// Color: verde <70, ámbar 70-89, rojo ≥90. Si pct es null/undefined → "—".
function MetricBar({ label, pct, hint }) {
  const has   = typeof pct === 'number' && Number.isFinite(pct)
  const val   = has ? Math.max(0, Math.min(100, Math.round(pct))) : 0
  const color = !has         ? '#6b7280'
              : val >= 90    ? '#ef4444'
              : val >= 70    ? '#f59e0b'
              : '#10b981'
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 10, marginBottom: 3,
      }}>
        <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</span>
        <span style={{ color, fontWeight: 700 }}>
          {has ? `${val}%` : '—'}
          {hint && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>{hint}</span>}
        </span>
      </div>
      <div style={{
        height: 5, borderRadius: 3,
        background: 'var(--bg-muted)', overflow: 'hidden',
      }}>
        <div style={{
          width: `${val}%`, height: '100%', background: color,
          transition: 'width 0.4s ease, background 0.3s ease',
        }} />
      </div>
    </div>
  )
}

function formatUptime(sec) {
  if (!sec || sec < 0) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function timeSince(iso) {
  if (!iso) return '—'
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (diff < 0)   return 'ahora'
  if (diff < 60)  return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff/60)}m`
  if (diff < 86400) return `${Math.floor(diff/3600)}h`
  return `${Math.floor(diff/86400)}d`
}

function HealthPanel({ site, token }) {
  const [devices, setDevices] = useState([])
  const [edge,    setEdge]    = useState(null)   // EdgeNode del sitio (cpu/mem/disk/uptime)
  const [loading, setLoading] = useState(true)

  // Carga dispositivos + nodo edge en paralelo
  const load = useCallback(async () => {
    if (!site) return
    try {
      const [rDev, rNode] = await Promise.all([
        fetch(`/api/devices/by-site/${site.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/edge/nodes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])
      const devs  = rDev.ok  ? await rDev.json()  : []
      const nodes = rNode.ok ? await rNode.json() : []
      setDevices(devs)
      // Match por site.id (FK). Si hay >1 nodo en el mismo sitio toma el más
      // reciente por last_heartbeat (caso raro pero defensivo).
      const cand = nodes
        .filter(n => n.site_id === site.id)
        .sort((a,b) => (b.last_heartbeat || '').localeCompare(a.last_heartbeat || ''))
      setEdge(cand[0] || null)
    } finally {
      setLoading(false)
    }
  }, [site, token])

  useEffect(() => {
    setLoading(true)
    setDevices([])
    setEdge(null)
    load()
  }, [load])

  // Refresh periódico: heartbeat suele ser cada ~60s, polleamos cada 15s para
  // que la barra refleje el último valor sin abrir WS dedicado.
  useEffect(() => {
    if (!site) return
    const id = setInterval(load, 15000)
    return () => clearInterval(id)
  }, [load, site?.id])

  const cams     = devices.filter(d => d.type === 'camera')
  const nvrs     = devices.filter(d => d.type === 'nvr')
  const speakers = devices.filter(d => d.type === 'speaker')
  const panels   = devices.filter(d => d.type === 'panel')
  const sensors  = devices.filter(d => d.type === 'sensor')

  const online  = devices.filter(d => d.online).length
  const total   = devices.length
  const pct     = total > 0 ? Math.round((online / total) * 100) : 100
  const pctColor = pct === 100 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#ef4444'
  const pctLabel = pct === 100 ? 'Óptimo' : pct >= 75 ? 'Operacional' : pct >= 50 ? 'Degradado' : 'Crítico'

  function rowColor(on, tot) {
    if (tot === 0) return 'var(--text-muted)'
    return on === tot ? '#10b981' : on === 0 ? '#ef4444' : '#f59e0b'
  }

  return (
    <div style={PANEL_STYLE}>
      <PanelHeader icon="💡" title="Salud del sistema" />

      {/* Índice global */}
      <div style={{
        padding: '12px 14px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
      }}>
        <div style={{
          width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
          border: `3px solid ${pctColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, color: pctColor,
        }}>{pct}%</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: pctColor }}>{pctLabel}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
            {loading ? tCem('Cargando…','Loading…') : `${online} / ${total} ${tCem('dispositivos en línea','devices online')}`}
          </div>
          {site.active_incidents > 0 && (
            <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>
              {site.active_incidents} incidente{site.active_incidents !== 1 ? 's' : ''} abierto{site.active_incidents !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {loading
        ? <Spinner />
        : (
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 12px' }}>
            {/* ── Edge Node: estado + CPU/MEM/Disk del Mac mini ──────────── */}
            <div style={{
              padding: '8px 0 10px 0',
              borderBottom: '1px solid var(--border)',
              marginBottom: 6,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>⬡</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Edge Node
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 1 }}>
                    {edge
                      ? `${edge.version || 'v?'} · uptime ${formatUptime(edge.uptime_s)} · hb ${timeSince(edge.last_heartbeat)}`
                      : (site.edge_online ? 'Sin métricas aún' : 'Sin nodo registrado')}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                  color: (edge?.online ?? site.edge_online) ? '#10b981' : '#ef4444',
                }}>
                  {(edge?.online ?? site.edge_online) ? 'En línea' : 'Offline'}
                </span>
              </div>

              {edge ? (
                <>
                  <MetricBar label="CPU"  pct={edge.cpu_pct}  />
                  <MetricBar label="MEM"  pct={edge.mem_pct}  />
                  <MetricBar label="Disk" pct={edge.disk_pct} />
                  {typeof edge.pending_events === 'number' && edge.pending_events > 0 && (
                    <div style={{
                      fontSize: 9, color: '#f59e0b', marginTop: 4,
                      display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span>Eventos pendientes</span>
                      <span style={{ fontWeight: 700 }}>{edge.pending_events}</span>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {cams.length > 0 && (
              <HealthRow
                icon="◉" label="Cámaras"
                value={`${cams.filter(d => d.online).length} / ${cams.length}`}
                color={rowColor(cams.filter(d => d.online).length, cams.length)}
              />
            )}

            {nvrs.length > 0 && (
              <HealthRow
                icon="▤" label="NVR"
                value={`${nvrs.filter(d => d.online).length} / ${nvrs.length}`}
                color={rowColor(nvrs.filter(d => d.online).length, nvrs.length)}
              />
            )}

            {speakers.length > 0 && (
              <HealthRow
                icon="♪" label="Bocinas"
                value={`${speakers.filter(d => d.online).length} / ${speakers.length}`}
                color={rowColor(speakers.filter(d => d.online).length, speakers.length)}
              />
            )}

            {panels.length > 0 && (
              <HealthRow
                icon="▣" label="Paneles"
                value={`${panels.filter(d => d.online).length} / ${panels.length}`}
                color={rowColor(panels.filter(d => d.online).length, panels.length)}
              />
            )}

            {sensors.length > 0 && (
              <HealthRow
                icon="⬡" label="Sensores"
                value={`${sensors.filter(d => d.online).length} / ${sensors.length}`}
                color={rowColor(sensors.filter(d => d.online).length, sensors.length)}
              />
            )}

            {/* Info del sitio */}
            {(site.address || site.zone_name) && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                {site.zone_name && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>
                    🏪 {site.zone_name}
                  </div>
                )}
                {site.address && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    📍 {site.address}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      }
    </div>
  )
}

// ─── Overlay sobre el mapa en modo split ──────────────────────────────────────

function MapSplitOverlay({ site, onClose, onCall }) {
  const color = STATUS_COLOR[site.status] || STATUS_COLOR.ok
  return (
    <>
      {/* Badge sitio — esquina sup-izq */}
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 5,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '5px 10px',
        display: 'flex', alignItems: 'center', gap: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        fontSize: 11, fontWeight: 600, maxWidth: 'calc(100% - 70px)',
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: color, flexShrink: 0,
          boxShadow: site.status === 'alarm' ? `0 0 6px ${color}` : 'none',
        }} />
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{site.name}</span>
      </div>

      {/* Acciones — esquina sup-der */}
      <div style={{
        position: 'absolute', top: 10, right: 10, zIndex: 5,
        display: 'flex', gap: 6,
      }}>
        {/* Botón llamada PTT */}
        <button
          onClick={() => onCall?.(site)}
          title="Llamar por radio PTT"
          style={{
            background: 'var(--success)', border: 'none',
            borderRadius: 6, padding: '5px 10px',
            cursor: 'pointer', fontSize: 11, color: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', gap: 4,
            fontWeight: 600,
          }}
        >
          <Phone size={11} /> Radio
        </button>

        {/* Botón cerrar */}
        <button
          onClick={onClose}
          title="Volver al mapa completo"
          style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '5px 10px',
            cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <span>←</span>
          <span>Mapa completo</span>
        </button>
      </div>
    </>
  )
}

// ─── FilterBar (solo modo completo) ───────────────────────────────────────────

function FilterBar({ filter, setFilter, counts }) {
  const filters = [
    { key: 'all',     label: 'Todos',       count: counts.all },
    { key: 'alarm',   label: tCem('Alarma','Alarm'),      count: counts.alarm,   color: STATUS_COLOR.alarm   },
    { key: 'warning', label: tCem('Advertencia','Warning'), count: counts.warning, color: STATUS_COLOR.warning },
    { key: 'offline', label: 'Offline',     count: counts.offline, color: STATUS_COLOR.offline },
    { key: 'ok',      label: 'Normal',      count: counts.ok,      color: STATUS_COLOR.ok      },
  ]
  return (
    <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 5, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {filters.map(f => (
        <button
          key={f.key}
          onClick={() => setFilter(f.key)}
          style={{
            padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
            background: filter === f.key ? (f.color || 'var(--accent)') : 'var(--bg-surface)',
            color:      filter === f.key ? '#fff' : 'var(--text-secondary)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'all 0.15s',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          {f.color && (
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: filter === f.key ? '#fff' : f.color, flexShrink: 0,
            }} />
          )}
          {f.label}
          {f.count > 0 && (
            <span style={{
              background: filter === f.key ? 'rgba(255,255,255,0.3)' : 'var(--bg-muted)',
              borderRadius: 10, padding: '0 5px', fontSize: 10,
            }}>{f.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

function Legend() {
  return (
    <div style={{
      position: 'absolute', bottom: 32, left: 12, zIndex: 5,
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      {Object.entries(STATUS_LABEL).map(([key, label]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[key], flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</span>
        </div>
      ))}
    </div>
  )
}

// ─── MapView principal ────────────────────────────────────────────────────────

export default function MapView({ wsMessage, onCallSite }) {
  const token      = useAuthStore(s => s.token)
  const mapRef     = useRef(null)
  const mapObj     = useRef(null)
  const markersRef = useRef({})

  const [sites,    setSites]    = useState([])
  const [selected, setSelected] = useState(null)
  const [filter,   setFilter]   = useState('all')
  const [loaded,   setLoaded]   = useState(false)

  // Estado compartido entre Q1 (video activo) y Q4 (plano navegable).
  // Sprint planograma 2026-06-12: click en cámara del plano cambia el video.
  const [selectedCameraId, setSelectedCameraId] = useState(null)
  const [siteIncidents,    setSiteIncidents]    = useState([])
  const [streamQuality,    setStreamQuality]    = useState(() => {
    try { return localStorage.getItem('retaliq_map_quality') || 'main' }
    catch { return 'main' }
  })
  useEffect(() => {
    try { localStorage.setItem('retaliq_map_quality', streamQuality) } catch {}
  }, [streamQuality])

  // Resetear la cámara activa cuando cambia el sitio seleccionado
  useEffect(() => {
    setSelectedCameraId(null)
  }, [selected?.id])

  // Cargar incidentes activos del sitio para Q2 + markers pulsantes en Q4
  useEffect(() => {
    if (!selected?.id || !token) {
      setSiteIncidents([])
      return
    }
    let cancelled = false
    const fetchIncidents = async () => {
      try {
        const r = await fetch(`/api/incidents?site_id=${selected.id}&status=open`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) return
        const data = await r.json()
        if (!cancelled) {
          setSiteIncidents(Array.isArray(data) ? data : (data.items || []))
        }
      } catch {}
    }
    fetchIncidents()
    const t = setInterval(fetchIncidents, 20000)
    return () => { cancelled = true; clearInterval(t) }
  }, [selected?.id, token])

  // ── Cargar sitios ─────────────────────────────────────────────────────────
  const loadSites = useCallback(async () => {
    const r = await fetch('/api/sites/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (r.ok) {
      const data = await r.json()
      setSites(data)
      // Actualizar site seleccionado si sus datos cambiaron
      setSelected(prev => prev ? (data.find(s => s.id === prev.id) || prev) : null)
    }
  }, [token])

  useEffect(() => { loadSites() }, [loadSites])
  useEffect(() => {
    const id = setInterval(loadSites, 30000)
    return () => clearInterval(id)
  }, [loadSites])

  useEffect(() => {
    if (!wsMessage) return
    if (wsMessage.type === 'incident_created' || wsMessage.type === 'incident_updated') {
      loadSites()
    }
  }, [wsMessage, loadSites])

  // ── Inicializar mapa ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || mapObj.current) return
    injectStyles()

    // Fix MapView #286 — el style vectorial de CARTO (positron-gl-style)
    // dejó de devolver tiles .mvt válidos: el style.json carga, los fonts
    // y sprite también, pero los tiles vectoriales nunca llegan → mapa en
    // blanco. Pasamos a tiles raster directos de CARTO Light, que son más
    // estables y no requieren style.json remoto. Mismo look (Positron Light).
    const map = new maplibregl.Map({
      container: mapRef.current,
      style: {
        version: 8,
        sources: {
          'carto-light': {
            type: 'raster',
            tiles: [
              'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
              'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
              'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
              'https://d.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
            ],
            tileSize: 256,
            attribution:
              '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' +
              ' · © <a href="https://carto.com/attributions">CARTO</a>',
          },
        },
        layers: [
          { id: 'carto-light', type: 'raster', source: 'carto-light' },
        ],
      },
      center: DEFAULT_CENTER,
      zoom:   DEFAULT_ZOOM,
      attributionControl: false,
    })
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')
    map.on('load', () => { mapObj.current = map; setLoaded(true) })
    return () => { map.remove(); mapObj.current = null }
  }, [])

  // ── Resize mapa cuando cambia el layout ──────────────────────────────────
  useEffect(() => {
    const map = mapObj.current
    if (!map) return
    // Doble RAF para asegurar que el browser ya repintó el nuevo layout
    let raf1 = requestAnimationFrame(() => {
      let raf2 = requestAnimationFrame(() => {
        map.resize()
      })
      return () => cancelAnimationFrame(raf2)
    })
    return () => cancelAnimationFrame(raf1)
  }, [selected])

  // ── Markers ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapObj.current
    if (!map || !loaded) return

    const visible    = filter === 'all' ? sites : sites.filter(s => s.status === filter)
    const visibleIds = new Set(visible.map(s => s.id))

    Object.keys(markersRef.current).forEach(id => {
      if (!visibleIds.has(Number(id))) {
        markersRef.current[id].marker.remove()
        delete markersRef.current[id]
      }
    })

    visible.forEach(site => {
      if (!site.lat || !site.lon) return
      const existing = markersRef.current[site.id]
      if (existing) {
        if (existing.status === site.status) return
        existing.marker.remove()
        delete markersRef.current[site.id]
      }

      const el     = createMarkerEl(site.status, true)
      el.title     = site.name
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([site.lon, site.lat])
        .addTo(map)

      el.addEventListener('click', e => {
        e.stopPropagation()
        setSelected(site)
        map.flyTo({ center: [site.lon, site.lat], zoom: Math.max(map.getZoom(), 13), duration: 500 })
      })

      markersRef.current[site.id] = { marker, el, status: site.status }
    })

    // Fit bounds primera carga
    if (loaded && Object.keys(markersRef.current).length > 0 && sites.length > 0 && !selected) {
      const withCoords = sites.filter(s => s.lat && s.lon)
      if (withCoords.length > 1) {
        const lngs   = withCoords.map(s => s.lon)
        const lats   = withCoords.map(s => s.lat)
        const bounds = [
          [Math.min(...lngs) - 0.1, Math.min(...lats) - 0.1],
          [Math.max(...lngs) + 0.1, Math.max(...lats) + 0.1],
        ]
        map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 800 })
      } else if (withCoords.length === 1) {
        map.flyTo({ center: [withCoords[0].lon, withCoords[0].lat], zoom: 13 })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, filter, loaded])

  // ── Conteos ───────────────────────────────────────────────────────────────
  const counts = {
    all:     sites.length,
    alarm:   sites.filter(s => s.status === 'alarm').length,
    warning: sites.filter(s => s.status === 'warning').length,
    offline: sites.filter(s => s.status === 'offline').length,
    ok:      sites.filter(s => s.status === 'ok').length,
  }

  const noCoords = sites.length > 0 && sites.every(s => !s.lat && !s.lon)
  const isSplit  = Boolean(selected)

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/*
        Grid contenedor:
          - Sin selección: 1 col × 1 row  (mapa ocupa todo)
          - Con selección: 2 cols × 2 rows (Q2 Q1 / Q3 Q4)

        mapRef SIEMPRE queda en la misma posición del árbol React
        para que MapLibre no pierda su contexto GL.
      */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: isSplit ? '1fr 1fr' : '1fr',
        gridTemplateRows:    isSplit ? '1fr 1fr' : '1fr',
        gap: isSplit ? 1 : 0,
        background: isSplit ? 'var(--border)' : 'transparent',
        minHeight: 0,
        position: 'relative',
      }}>
        {/* ── Mapa principal de sitios ─────────────────────────────────────
            Sin selección: ocupa toda la grilla.
            Con selección (split): se oculta con display:none — el div
            queda en el DOM para que MapLibre no pierda su contexto WebGL,
            pero NO ocupa una celda del grid 2×2. En su lugar entran los
            4 cuadrantes (Incidentes + Vivo + Timeline + Planograma).
            Los botones flotantes de "← Mapa completo" y "Radio" se
            mueven a la barra superior del wrapper grid. */}
        <div style={{
          position: 'relative', overflow: 'hidden', minHeight: 0,
          display: isSplit ? 'none' : 'block',
        }}>
          {/* El div del mapa SIEMPRE en el DOM */}
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

          {/* Overlays modo completo */}
          {!isSplit && sites.length > 0 && (
            <FilterBar filter={filter} setFilter={setFilter} counts={counts} />
          )}
          {!isSplit && <Legend />}
          {!isSplit && noCoords && (
            <div style={{
              position: 'absolute', top: 12, right: 12, zIndex: 5,
              background: '#fef3c7', border: '1px solid #f59e0b',
              borderRadius: 8, padding: '8px 14px', fontSize: 11,
              color: '#92400e', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            }}>
              ⚠️ Los sitios no tienen coordenadas. Agrégalas en Configuración → Sitios.
            </div>
          )}

          {/* Overlay modo split — ya no se monta acá porque el div padre
              está display:none cuando isSplit. El control flotante de
              cerrar split se monta fuera, sobre el grid. */}
        </div>

        {/* Control flotante en split mode — reemplaza al MapSplitOverlay
            que vivía sobre el mapa (ahora oculto). Permite cerrar el split
            y llamar al sitio. Sticky arriba a la izquierda del grid. */}
        {isSplit && (
          <div style={{
            position: 'absolute', top: 8, left: 8, zIndex: 20,
            display: 'flex', gap: 6, alignItems: 'center',
            padding: '6px 10px',
            background: 'rgba(15, 23, 42, 0.92)',
            border: '1px solid var(--border)',
            borderRadius: 6, color: 'white', fontSize: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '2px 8px', borderRadius: 999,
              background: selected?.status === 'alarm' ? '#d93025'
                : selected?.status === 'warning' ? '#f59e0b'
                : selected?.status === 'offline' ? '#6b7280'
                : '#10b981',
              fontSize: 11, fontWeight: 600,
            }}>
              {selected?.name}
            </span>
            <button
              onClick={() => setSelected(null)}
              style={{
                padding: '3px 10px', fontSize: 11,
                background: 'transparent', color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 3, cursor: 'pointer',
              }}
              title="Volver al mapa completo"
            >
              ← Mapa completo
            </button>
            {onCallSite && (
              <button
                onClick={() => onCallSite(selected)}
                style={{
                  padding: '3px 10px', fontSize: 11,
                  background: '#10b981', color: 'white',
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                  fontWeight: 600,
                }}
                title="Radio al sitio"
              >
                Radio
              </button>
            )}
          </div>
        )}

        {/* Sprint planograma 2026-06-12: los 4 cuadrantes rediseñados.
            Q1 = video + salud unificados (estado compartido selectedCameraId).
            Q2 = incidentes activos del sitio con clip 6s + acciones rápidas.
            Q3 = timeline del día con eventos agrupados por hora.
            Q4 = plano georreferenciado navegable, click cámara → cambia Q1. */}
        {/* Cada cuadrante en ErrorBoundary independiente — si uno crashea
            por shape inesperado del backend (ej. "vehicle_summary" undefined),
            los otros 3 siguen funcionando. */}
        {/* Orden de los cuadrantes en split mode (grid 2×2 row-flow):
            top-left  → Incidentes (reemplaza al mapa de sitios)
            top-right → Vivo
            bot-left  → Timeline
            bot-right → Planograma                                          */}
        {isSplit && (
          <ErrorBoundary view="Cuadrante Incidentes">
            <MapViewQ2Incidents
              site={selected}
              incidents={siteIncidents}
              onSelectIncident={(inc) => {
                if (inc.camera_id) setSelectedCameraId(inc.camera_id)
              }}
            />
          </ErrorBoundary>
        )}

        {isSplit && (
          <ErrorBoundary view="Cuadrante Vivo">
            <MapViewQ1Unified
              site={selected}
              token={token}
              cameraId={selectedCameraId}
              streamQuality={streamQuality}
              onChangeQuality={setStreamQuality}
            />
          </ErrorBoundary>
        )}

        {isSplit && (
          <ErrorBoundary view="Cuadrante Timeline">
            <MapViewQ3Timeline
              site={selected}
              token={token}
              onJumpTo={(ts) => {
                window.open(
                  `/monitor?site=${selected.id}&tab=forense&t=${encodeURIComponent(ts)}`,
                  `mon_${selected.id}`, 'noopener',
                )
              }}
            />
          </ErrorBoundary>
        )}

        {isSplit && (
          <ErrorBoundary view="Cuadrante Plano">
            <MapViewQ4Planogram
              site={selected}
              token={token}
              selectedCameraId={selectedCameraId}
              onCameraSelect={setSelectedCameraId}
              incidents={siteIncidents}
              onOpenConfig={() => {
                window.open('/site-config', '_blank', 'noopener')
              }}
            />
          </ErrorBoundary>
        )}
      </div>

      {/* Estado vacío — sin sitios */}
      {sites.length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '24px 32px', textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📍</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{tCem('Sin sitios configurados','No sites configured')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Ve a Configuración → Sitios para agregar ubicaciones
            </div>
          </div>
        </div>
      )}
    </div>
  )
}